<script lang="ts" setup>
import { Form as VeeForm, FormContext } from 'vee-validate'

import { useBasketStore } from '@/store/basket/basket'
import { useCityStore } from '@/store/city/city'
import { useUserStore } from '@/store/user/user'

import { BasketStep } from '@/types/basket'

import BasketAdditive from '@/components/Basket/basket-additive/BasketAdditive.vue'
import BasketAddresses from '@/components/Basket/basket-addresses/BasketAddresses.vue'
import BasketClientPack from '@/components/Basket/basket-client-pack/BasketClientPack.vue'
import BasketDiscount from '@/components/Basket/basket-discount/BasketDiscount.vue'
import BasketGifts from '@/components/Basket/basket-gifts/BasketGifts.vue'
import BasketNote from '@/components/Basket/basket-note/BasketNote.vue'
import BasketOrder from '@/components/Basket/basket-order/BasketOrder.vue'
import BasketPricePanel from '@/components/Basket/basket-price-panel/BasketPricePanel.vue'
import BasketPriceShortPanel from '@/components/Basket/basket-price-panel/BasketPriceShortPanel.vue'

const DialogBasketAdditivesAndPacks = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/DialogBasketAdditivesAndPacks.vue'),
)
const LoginForm = defineAsyncComponent(
  () => import('@/components/login/LoginForm.vue'),
)

type Props = {
  clientPacksCount: number
  clientAdditivesCount: number
}

const MT_EVENT_NAME = 'startofordering'

const emit = defineEmits(['goToStep', 'scrollToErrorBlock'])
const props = defineProps<Props>()

const { $metrics } = useNuxtApp()

const basketStore = useBasketStore()
const userStore = useUserStore()
const { currencySymbol } = useCityStore()

const basketObserver = ref<FormContext | null>(null)
const isGiftNeed = ref(false)
const isPriceSectionSticky = ref(true)
const dialogs = ref({
  additivesAndPacks: {
    visible: false,
  },
  userAuthorization: {
    visible: false,
  },
})

const isClientPackAvailable = computed(() =>
  !basketStore.data.persons ? true : basketStore.data.persons.maxNumber === 0,
)
const basket = computed(() => basketStore.data)
const isAuthenticated = computed(() => userStore.isAuthenticated)
const basketGiftsWithoutChoice = computed(() => basketStore.giftsNoChoice)
const basketGiftsWithChoice = computed(() => basketStore.giftsOfChoice)
const addedAdditives = computed(() => basketStore.addedAdditives)
const amountToPayWithSymbol = computed(
  () => `${basket.value.amountToPay} ${currencySymbol}`,
)

const setPricePanelStickyObserver = () => {
  useIntersectionObserver(
    '#price-panel',
    {
      threshold: 0.5,
    },
    ([{ isIntersecting, boundingClientRect }]) => {
      isPriceSectionSticky.value =
        !isIntersecting && boundingClientRect.top > 500
    },
  )
}

// Функция для получения значения из localStorage
const getShowedAdditivesAndPacksValue = (): boolean => {
  const localStorageValue = localStorage.getItem(
    'showedWarningAdditivesOrClientPack',
  )

  if (!localStorageValue) return false

  return JSON.parse(localStorageValue)
}

// Функция для обработки показа диалогового окна добавок и приборов
const showAdditivesAndPacksDialog = (callback: () => void) => {
  // Получаем флаг из localStorage
  const isAdditivesAndClientPackModalShowed = getShowedAdditivesAndPacksValue()
  // Проверяем, добавил ли клиент добавки или приборы
  const isAdditivesOrPacksNotAdded =
    !props.clientAdditivesCount || !props.clientPacksCount

  /*
    Проверяем, показывали ли мы пользователю диалоговое окно с приборами и добавками
    Так же проверяем,
  */
  if (!isAdditivesAndClientPackModalShowed && isAdditivesOrPacksNotAdded) {
    dialogs.value.additivesAndPacks.visible = true

    return
  }

  // Выполняем переданный коллбек при достижении всех условий
  callback()
}

const goToSecondStep = () => {
  // Если клиент не авторизован, то сначала показываем диалоговое окно авторизации
  if (!isAuthenticated.value) {
    dialogs.value.userAuthorization.visible = true

    return
  }

  showAdditivesAndPacksDialog(() => {
    emit('goToStep', BasketStep.SECOND)
  })
}

const goToAuthorize = () => {
  showAdditivesAndPacksDialog(() => {
    dialogs.value.userAuthorization.visible = true
  })
}

const goToCatalog = () => navigateTo('/')

const userAuthorizationDialogClose = () => {
  dialogs.value.userAuthorization.visible = false
}

// Скролл до блока с ошибкой
const showError = () =>
  setTimeout(() => {
    const fieldError = getFieldError()

    scrollToErrorBlock(fieldError)
  }, 0)

// Функция для получения id блока, в котором есть ошибка валидации
const getFieldError = () => {
  const fieldsErrors = basketObserver.value?.errors

  if (!fieldsErrors) return null

  return Object.keys(fieldsErrors).at(0) || null
}

const scrollToErrorBlock = (errorBlock?: string | null) => {
  emit('scrollToErrorBlock', errorBlock)
}

onMounted(() => {
  setPricePanelStickyObserver()

  $metrics?.eventSendGoal({ mt: MT_EVENT_NAME })
})
</script>

<template>
  <vee-form
    v-slot="{ handleSubmit }"
    ref="basketObserver"
    class="basket__section"
    validate-on-mount
  >
    <basket-order
      class="section order"
      :items="basket.items"
      :gifts-without-choice="basketGiftsWithoutChoice"
      :additives="addedAdditives"
    />

    <basket-client-pack
      v-if="isClientPackAvailable"
      :client-packs="basket.clientPacks"
      :client-packs-count="clientPacksCount"
      class="section"
    />

    <basket-additive
      v-if="basket.additives.length > 0"
      id="additives"
      :additives="basket.additives"
      class="section additives"
      @go-to-second-step="goToSecondStep"
    />

    <basket-gifts
      v-if="basketGiftsWithChoice.length !== 0"
      v-model="isGiftNeed"
      class="section"
      :gifts="basketGiftsWithChoice"
    />

    <basket-note class="section" />

    <basket-addresses v-if="isAuthenticated" class="section" />

    <basket-discount class="section" />

    <transition name="ui-slide-up">
      <div
        v-if="isPriceSectionSticky"
        class="section section--sticky price-button"
      >
        <basket-price-short-panel
          :amount-to-pay="amountToPayWithSymbol"
          :is-loading="basketStore.isLoading"
          :is-gift-need="isGiftNeed"
          :basket-step="BasketStep.FIRST"
          :cost="basket.delivery.cost"
          :is-price-section-sticky="true"
          :is-authenticated="isAuthenticated"
          @go-to-catalog="goToCatalog"
          @go-to-authorize="goToAuthorize"
          @go-to-address="scrollToErrorBlock('addresses')"
          @go-to-discount="scrollToErrorBlock('discount')"
          @go-to-gifts="scrollToErrorBlock('gifts')"
          @go-to-second-step="handleSubmit(goToSecondStep), showError()"
        />
      </div>
    </transition>

    <basket-price-panel
      :basket-step="BasketStep.FIRST"
      :amount-to-pay-with-symbol="amountToPayWithSymbol"
      :cost="basket.delivery.cost"
      :is-authenticated="isAuthenticated"
      :is-gift-need="isGiftNeed"
      :is-loading="basketStore.isLoading"
      :is-price-section-sticky="false"
      class="section price"
      @go-to-catalog="goToCatalog"
      @go-to-authorize="goToAuthorize"
      @go-to-address="scrollToErrorBlock('addresses')"
      @go-to-discount="scrollToErrorBlock('discount')"
      @go-to-gifts="scrollToErrorBlock('gifts')"
      @go-to-second-step="handleSubmit(goToSecondStep), showError()"
    />
  </vee-form>

  <!-- Диалоговое окно добавок и клиент пакетов -->
  <dialog-basket-additives-and-packs
    v-model="dialogs.additivesAndPacks.visible"
    :client-packs="basket.clientPacks"
    :client-packs-count="clientPacksCount"
    :additives="basket.additives"
    :additives-count="clientAdditivesCount"
    @go-to-second-step="goToSecondStep"
  />

  <the-dialog v-model="dialogs.userAuthorization.visible" :max-width="560">
    <login-form
      @code-confirmed-by-named-user="userAuthorizationDialogClose"
      @set-name="userAuthorizationDialogClose"
      @sign-in-by-named-user="userAuthorizationDialogClose"
    />
  </the-dialog>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/basket' as *;
</style>
